import { useState, useEffect } from 'react';
import { useAuth } from '../providers/AuthProvider';
import { useDemoMode } from '../providers/DemoModeProvider';

interface MockDataInfo {
  hasMockData: boolean;
  dataTypes: string[];
  userHasRealData: boolean;
  onboardingCompleted: boolean;
  shouldShowBanner: boolean;
  guidanceMessage: string;
}

export function useMockDataDetection(pageType: string): MockDataInfo {
  const { user } = useAuth();
  const { showDemoData } = useDemoMode();
  const [mockDataInfo, setMockDataInfo] = useState<MockDataInfo>({
    hasMockData: false,
    dataTypes: [],
    userHasRealData: false,
    onboardingCompleted: false,
    shouldShowBanner: false,
    guidanceMessage: ''
  });

  useEffect(() => {
    // Check if user has completed onboarding
    const onboardingCompleted = localStorage.getItem('zippyhealth_onboarding_complete') === 'true';
    
    // Check if user has real data
    const hasRealConditions = localStorage.getItem('user_conditions') && 
                             JSON.parse(localStorage.getItem('user_conditions') || '[]').length > 0;
    const hasRealMedications = localStorage.getItem('user_medications') && 
                               JSON.parse(localStorage.getItem('user_medications') || '[]').length > 0;
    const hasRealSymptoms = localStorage.getItem('user_symptoms') && 
                            JSON.parse(localStorage.getItem('user_symptoms') || '[]').length > 0;
    const hasRealFood = localStorage.getItem('user_food_entries') && 
                        JSON.parse(localStorage.getItem('user_food_entries') || '[]').length > 0;
    
    const userHasRealData = hasRealConditions || hasRealMedications || hasRealSymptoms || hasRealFood;
    
    // Determine if this page typically shows mock data
    const mockDataPages = {
      'dashboard': ['health-conditions', 'medications', 'symptoms', 'food'],
      'conditions': ['health-conditions'],
      'medications': ['medications'],
      'symptoms': ['symptoms'],
      'food-tracking': ['food'],
      'meal-log': ['food'],
      'analytics': ['health-conditions', 'medications', 'symptoms', 'food'],
      'guru-connect': ['general'],
      'health-marketplace': ['general'],
      'trust-score': ['general'],
      'recipe-book': ['food'],
      'meal-planner': ['food']
    };
    
    const dataTypes = mockDataPages[pageType as keyof typeof mockDataPages] || [];
    const hasMockData = dataTypes.length > 0 && (showDemoData || !userHasRealData);
    
    // Determine guidance message
    let guidanceMessage = '';
    if (!onboardingCompleted) {
      guidanceMessage = 'Complete onboarding to personalize your experience';
    } else if (!userHasRealData) {
      guidanceMessage = 'Add your health data to get personalized insights';
    } else if (showDemoData) {
      guidanceMessage = 'Demo mode is active - switch to your real data';
    }
    
    const shouldShowBanner = hasMockData && !userHasRealData && !showDemoData;
    
    setMockDataInfo({
      hasMockData,
      dataTypes,
      userHasRealData,
      onboardingCompleted,
      shouldShowBanner,
      guidanceMessage
    });
  }, [pageType, showDemoData, user]);

  return mockDataInfo;
}

export function useOnboardingGuidance() {
  const { user } = useAuth();
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(false);
  
  useEffect(() => {
    const onboardingCompleted = localStorage.getItem('zippyhealth_onboarding_complete') === 'true';
    const hasSeenPrompt = localStorage.getItem('onboarding_prompt_shown') === 'true';
    
    if (!onboardingCompleted && !hasSeenPrompt && user) {
      setShowOnboardingPrompt(true);
      localStorage.setItem('onboarding_prompt_shown', 'true');
    }
  }, [user]);

  const dismissOnboardingPrompt = () => {
    setShowOnboardingPrompt(false);
  };

  const startOnboarding = () => {
    setShowOnboardingPrompt(false);
    // Trigger onboarding flow
    window.dispatchEvent(new CustomEvent('start-onboarding'));
  };

  return {
    showOnboardingPrompt,
    dismissOnboardingPrompt,
    startOnboarding
  };
} 